/**
 * Pure Y-axis zoom / pan math for the EGM Details AreaChart.
 *
 * Auto-fit is [0, dataMax × (1 + pad)]. The view is a window [min, max] with:
 * - min ≥ 0 (never below zero — not pinned to zero)
 * - max ≤ autoFitMax
 * - span ∈ [autoFitMax/50, autoFitMax]
 *
 * Axis drag zooms around the finger; plot drag slides the window at constant span.
 */

export const Y_ZOOM_MAX = 50;
export const Y_DOMAIN_PAD = 0.05;
export const POINTER_THRESHOLD_PX = 6;
export const DOUBLE_TAP_MS = 300;

export type YAxisView = {
  min: number;
  max: number;
};

export type InsightAxisCategory = 'percent' | 'dollars' | 'count';

/** Auto-fit ceiling: data max plus ~5%. Zero / empty data falls back to 1. */
export const computeAutoFitMax = (dataMax: number, pad = Y_DOMAIN_PAD): number => {
  if (!Number.isFinite(dataMax) || dataMax <= 0) return 1;
  return dataMax * (1 + pad);
};

export const autoFitView = (dataMax: number, pad = Y_DOMAIN_PAD): YAxisView => ({
  min: 0,
  max: computeAutoFitMax(dataMax, pad),
});

export const minSpan = (autoFitMax: number): number =>
  Math.max(autoFitMax / Y_ZOOM_MAX, Number.EPSILON);

/**
 * Clamp a view into the legal rectangle:
 * min ≥ 0, max ≤ autoFitMax, span ∈ [autoFitMax/50, autoFitMax].
 */
export const clampView = (view: YAxisView, autoFitMax: number): YAxisView => {
  const hi = Math.max(autoFitMax, Number.EPSILON);
  const spanMin = minSpan(hi);
  let span = view.max - view.min;
  if (!Number.isFinite(span) || span <= 0) span = hi;
  span = Math.min(hi, Math.max(spanMin, span));

  let min = Number.isFinite(view.min) ? view.min : 0;
  let max = min + span;
  if (min < 0) {
    min = 0;
    max = span;
  }
  if (max > hi) {
    max = hi;
    min = Math.max(0, max - span);
  }
  return { min, max };
};

/** @deprecated Prefer clampView — zero-based ceiling helper for tests. */
export const clampViewMax = (viewMax: number, autoFitMax: number): number =>
  clampView({ min: 0, max: viewMax }, autoFitMax).max;

export const minZoomMax = minSpan;

export const isZoomedView = (view: YAxisView, autoFitMax: number, eps = 1e-6): boolean =>
  view.min > eps || view.max < autoFitMax - eps;

/** Visible span of a view. */
export const viewSpan = (view: YAxisView): number =>
  Math.max(view.max - view.min, Number.EPSILON);

/**
 * Data value at a pixel Y (0 = top of plot, height = bottom).
 * Domain is [view.min, view.max].
 */
export const valueAtPlotY = (plotY: number, height: number, view: YAxisView): number => {
  if (height <= 0) return view.min;
  const t = 1 - plotY / height; // 0 at bottom, 1 at top
  return view.min + viewSpan(view) * t;
};

/**
 * Axis drag zoom anchored at the finger: the data value under the pointer at
 * drag-start stays at that pixel while the span changes. Result is clamped so
 * min ≥ 0 and max ≤ autoFitMax.
 *
 * `deltaY` is pointer movement in CSS pixels (positive = finger moved down → zoom out).
 */
export const zoomViewByAxisDrag = (
  view: YAxisView,
  autoFitMax: number,
  height: number,
  anchorY: number,
  deltaY: number
): YAxisView => {
  const current = clampView(view, autoFitMax);
  if (height <= 0) return current;

  const span = viewSpan(current);
  const anchorT = Math.min(0.98, Math.max(0.02, 1 - anchorY / height));
  const anchorValue = current.min + span * anchorT;

  // Finger down → larger span (zoom out); finger up → smaller span (zoom in).
  const zoomFactor = Math.exp(deltaY / height);
  const nextSpan = span * zoomFactor;
  const nextMin = anchorValue - anchorT * nextSpan;
  return clampView({ min: nextMin, max: nextMin + nextSpan }, autoFitMax);
};

/**
 * Plot-area vertical pan while zoomed. Slides [min, max] at constant span.
 * Positive `deltaY` (finger down) moves the window toward higher values.
 * Clamped so min ≥ 0 and max ≤ autoFitMax.
 */
export const panViewByPlotDrag = (
  view: YAxisView,
  autoFitMax: number,
  height: number,
  deltaY: number
): YAxisView => {
  const current = clampView(view, autoFitMax);
  if (height <= 0) return current;
  const span = viewSpan(current);
  // Finger down → content follows down → top of plot shows higher data.
  const dataDelta = (deltaY / height) * span;
  return clampView(
    { min: current.min + dataDelta, max: current.max + dataDelta },
    autoFitMax
  );
};

/** Nice step near `range / targetTicks` on a 1–2–5 ladder (bias toward more ticks). */
export const niceStep = (range: number, targetTicks = 4): number => {
  if (!Number.isFinite(range) || range <= 0) return 1;
  const rough = range / Math.max(1, targetTicks);
  const power = Math.pow(10, Math.floor(Math.log10(rough)));
  const scaled = rough / power;
  let nice: number;
  if (scaled <= 1) nice = 1;
  else if (scaled <= 2) nice = 2;
  else if (scaled <= 2.5) nice = 2.5;
  else if (scaled <= 5) nice = 5;
  else nice = 10;
  return nice * power;
};

/** Inclusive ticks from min to max using `step`. */
export const buildTicks = (min: number, max: number, step: number): number[] => {
  if (!Number.isFinite(max) || !Number.isFinite(min) || max <= min) {
    return [Number.isFinite(min) ? min : 0];
  }
  const s = step > 0 ? step : niceStep(max - min);
  const start = Math.ceil((min - s * 1e-9) / s) * s;
  const ticks: number[] = [];
  for (let v = start; v <= max + s * 1e-9; v += s) {
    const rounded = Number((Math.round(v / s) * s).toPrecision(12));
    if (rounded < min - s * 1e-9) continue;
    if (rounded > max + s * 1e-9) break;
    ticks.push(rounded);
    if (ticks.length > 50) break;
  }
  if (ticks.length === 0 || Math.abs(ticks[0] - min) > s * 0.05) {
    ticks.unshift(Number(min.toPrecision(12)));
  }
  const last = ticks[ticks.length - 1] ?? min;
  if (max - last > s * 0.05) ticks.push(Number(max.toPrecision(12)));
  return ticks;
};

/** How many fraction digits a tick label needs so consecutive steps don't collide. */
export const decimalsForStep = (step: number): number => {
  if (!Number.isFinite(step) || step <= 0) return 0;
  if (Math.abs(step - Math.round(step)) < Math.max(step, 1) * 1e-9) return 0;
  const normalized = step.toFixed(10).replace(/0+$/, '');
  const dot = normalized.indexOf('.');
  return dot < 0 ? 0 : Math.min(6, normalized.length - dot - 1);
};

const trimTrailingZeros = (s: string): string =>
  s.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.0+$/, '');

/**
 * Format a Y tick so labels never repeat for the given step.
 * Examples: $1.5K, $1,250, $0.25 — and matching count / percent forms.
 */
export const formatYAxisTick = (
  val: number,
  category: InsightAxisCategory,
  step: number
): string => {
  if (!Number.isFinite(val)) return '';
  // Hide an exact zero baseline label so it doesn't crowd the X-axis ticks.
  if (Math.abs(val) < Math.max(step, 1) * 1e-9) return '';

  const abs = Math.abs(val);
  const sign = val < 0 ? '-' : '';

  if (category === 'percent') {
    const d = decimalsForStep(step);
    return `${sign}${abs.toFixed(d)}%`;
  }

  const useM = abs >= 1_000_000 && step >= 10_000;
  const useK = !useM && abs >= 1000 && step >= 500;

  let body: string;
  if (useM) {
    const inM = abs / 1_000_000;
    const stepM = step / 1_000_000;
    const d = Math.max(decimalsForStep(stepM), inM < 10 && stepM < 1 ? 1 : 0);
    body = `${trimTrailingZeros(inM.toFixed(d))}M`;
  } else if (useK) {
    const inK = abs / 1000;
    const stepK = step / 1000;
    const d = Math.max(decimalsForStep(stepK), stepK < 1 ? 1 : 0);
    body = `${trimTrailingZeros(inK.toFixed(d))}K`;
  } else {
    const d = decimalsForStep(step);
    if (d === 0) {
      body = Math.round(abs).toLocaleString('en-US');
    } else {
      const fixed = abs.toFixed(d);
      const [intPart, frac] = fixed.split('.');
      const grouped = Number(intPart).toLocaleString('en-US');
      body = frac != null ? trimTrailingZeros(`${grouped}.${frac}`) : grouped;
    }
  }

  if (category === 'dollars') return `${sign}$${body}`;
  return `${sign}${body}`;
};

/** True when a reference Y sits inside the visible domain (inclusive). */
export const isYInView = (y: number, view: YAxisView, eps = 1e-9): boolean =>
  y >= view.min - eps && y <= view.max + eps;

/**
 * Pixel top/height for a jackpot bar. Values above the view are capped at the
 * plot top (▲); values below the view produce no bar. When 0 is scrolled away,
 * bars grow from the visible floor.
 */
export const jackpotBarLayout = (
  value: number,
  view: YAxisView,
  chartTop: number,
  chartBottom: number
): { y: number; height: number; capped: boolean } => {
  const plotH = chartBottom - chartTop;
  if (plotH <= 0 || view.max <= view.min) {
    return { y: chartBottom, height: 0, capped: false };
  }
  const span = viewSpan(view);
  if (value < view.min - span * 1e-9) {
    return { y: chartBottom, height: 0, capped: false };
  }
  const capped = value > view.max + span * 1e-9;
  const clampedTop = Math.min(Math.max(value, view.min), view.max);
  const tTop = (clampedTop - view.min) / span;
  const yTop = capped ? chartTop : chartBottom - tTop * plotH;
  // Grow from data 0 when visible, otherwise from the visible floor.
  const baseline = Math.max(0, view.min);
  const tBase = (baseline - view.min) / span;
  const yBase = chartBottom - tBase * plotH;
  return {
    y: yTop,
    height: Math.max(0, yBase - yTop),
    capped,
  };
};
